import { io, Socket } from 'socket.io-client';

/**
 * Singleton WebSocket service for managing Socket.io connections
 * Handles authentication, reconnection, and event management
 */
class SocketService {
  private socket: Socket | null = null;
  private readonly serverUrl: string;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  constructor() {
    // Use REACT_APP_SOCKET_URL if available, otherwise fallback to API base URL
    this.serverUrl =
      process.env.REACT_APP_SOCKET_URL ||
      process.env.REACT_APP_API_BASE_URL ||
      'http://localhost:5000';
  }

  /**
   * Connect to Socket.io server with JWT authentication
   * @param token - JWT access token
   * @returns Promise that resolves when connected
   */
  connect(token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        console.log('✅ Socket already connected');
        resolve();
        return;
      }

      // Disconnect existing socket if any
      if (this.socket) {
        this.socket.disconnect();
      }

      // Create new socket connection with auth
      this.socket = io(this.serverUrl, {
        auth: {
          token,
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        reconnectionDelayMax: 5000,
        timeout: 20000,
      });

      // Connection successful
      this.socket.on('connect', () => {
        console.log('✅ Socket.io connected:', this.socket?.id);
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        resolve();
      });

      // Connection error
      this.socket.on('connect_error', (error: Error) => {
        console.error('❌ Socket connection error:', error.message);

        if (error.message.includes('Authentication error')) {
          // Auth failed - disconnect and notify
          this.disconnect();
          window.dispatchEvent(
            new CustomEvent('socket:auth_error', { detail: error.message })
          );
          reject(new Error('Socket authentication failed'));
        } else {
          this.reconnectAttempts++;

          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('❌ Max reconnection attempts reached');
            reject(new Error('Socket connection failed'));
          }
        }
      });

      // Disconnection
      this.socket.on('disconnect', (reason: string) => {
        console.log('🔌 Socket disconnected:', reason);

        if (reason === 'io server disconnect') {
          // Server initiated disconnect - reconnect manually
          const token = localStorage.getItem('authToken');
          if (token) {
            setTimeout(() => this.connect(token), this.reconnectDelay);
          }
        }

        window.dispatchEvent(
          new CustomEvent('socket:disconnected', { detail: reason })
        );
      });

      // Reconnection attempt
      this.socket.on('reconnect_attempt', (attempt: number) => {
        console.log(`🔄 Reconnection attempt ${attempt}/${this.maxReconnectAttempts}`);
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 5000);
      });

      // Reconnection successful
      this.socket.on('reconnect', (attemptNumber: number) => {
        console.log(`✅ Reconnected after ${attemptNumber} attempts`);
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        window.dispatchEvent(new CustomEvent('socket:reconnected'));
      });

      // Error handling
      this.socket.on('error', (error: Error) => {
        console.error('❌ Socket error:', error);
      });
    });
  }

  /**
   * Disconnect from Socket.io server
   */
  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnecting socket...');
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get socket ID
   */
  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  /**
   * Listen to an event
   * @param event - Event name
   * @param callback - Callback function
   */
  on(event: string, callback: (...args: any[]) => void): void {
    if (!this.socket) {
      console.warn('⚠️ Socket not initialized. Call connect() first.');
      return;
    }

    this.socket.on(event, callback);
  }

  /**
   * Remove event listener
   * @param event - Event name
   * @param callback - Optional specific callback to remove
   */
  off(event: string, callback?: (...args: any[]) => void): void {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }

  /**
   * Emit an event to the server
   * @param event - Event name
   * @param data - Data to send
   * @param callback - Optional acknowledgment callback
   */
  emit(event: string, data?: any, callback?: (...args: any[]) => void): void {
    if (!this.socket?.connected) {
      console.warn('⚠️ Socket not connected. Cannot emit event:', event);
      return;
    }

    if (callback) {
      this.socket.emit(event, data, callback);
    } else {
      this.socket.emit(event, data);
    }
  }

  /**
   * Emit an event with promise-based acknowledgment
   * @param event - Event name
   * @param data - Data to send
   * @returns Promise that resolves with the acknowledgment
   */
  emitWithAck<T = any>(event: string, data?: any): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error(`Timeout waiting for acknowledgment for event: ${event}`));
      }, 10000);

      this.socket.emit(event, data, (response: T) => {
        clearTimeout(timeout);
        resolve(response);
      });
    });
  }

  /**
   * Join a room
   * @param room - Room name
   */
  joinRoom(room: string): void {
    this.emit('join_room', { room });
  }

  /**
   * Leave a room
   * @param room - Room name
   */
  leaveRoom(room: string): void {
    this.emit('leave_room', { room });
  }
}

// Export singleton instance
export const socketService = new SocketService();
export default socketService;
